import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import ModeToggle from '../components/ModeToggle';

export default function StudyPlan() {
  const { API, mode, user } = useApp();
  const isCyber = mode === 'cyber';
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ weakest_subject: '', target_score: 30, test_date: '' });
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (!user) { setErr('Please sign in to save your plan.'); nav('/auth'); return; }
    try {
      const r = await axios.post(`${API}/study-plan/generate`, {
        weakest_subject: form.weakest_subject,
        target_score: Number(form.target_score),
        test_date: form.test_date,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('pp-token') || ''}` } });
      setPlan(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const cardCls = isCyber ? 'glass-cyber rounded-2xl p-8' : 'parchment rounded-2xl p-8';
  const btnPrimary = isCyber ? 'bg-[#00FFD1] text-black font-orbitron tracking-widest px-6 py-3 rounded-lg' : 'storybook-btn px-6 py-3 rounded-lg';

  return (
    <div className={`min-h-screen px-6 py-10 ${isCyber ? 'bg-black text-white' : 'text-[#2C2621]'}`}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <button data-testid="plan-back" onClick={() => nav('/')} className={isCyber ? 'font-mono-cyber text-[#00FFD1]' : 'font-body-story'}>← back</button>
          <ModeToggle />
        </div>
        <h1 className={isCyber ? 'font-orbitron text-3xl mb-8' : 'font-storybook text-4xl mb-8'}>
          {isCyber ? 'CUSTOM 5-WEEK STUDY PLAN' : 'Chart Your 5-Week Journey'}
        </h1>

        {!plan ? (
          <motion.div layout className={cardCls}>
            {step === 0 && (
              <>
                <div className="mb-4 text-lg">Which subject do you struggle with the most?</div>
                <div className="grid gap-3">
                  {['Math', 'English', 'Science'].map(s => (
                    <button key={s} data-testid={`plan-weakest-${s.toLowerCase()}`}
                      onClick={() => { setForm(f => ({ ...f, weakest_subject: s })); setStep(1); }}
                      className={`text-left px-5 py-4 rounded-xl border-2 ${isCyber ? 'border-neutral-800 hover:border-[#00FFD1]' : 'border-[#C9B99A] hover:border-[#8C1C13]'}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
            {step === 1 && (
              <>
                <div className="mb-4 text-lg">Target ACT score:</div>
                <input data-testid="plan-target-score" type="range" min="18" max="36" value={form.target_score}
                  onChange={e => setForm(f => ({ ...f, target_score: e.target.value }))}
                  className="w-full accent-[#00FFD1]" />
                <div className={`text-4xl mt-4 ${isCyber ? 'font-orbitron text-[#00FFD1]' : 'font-storybook text-[#8C1C13]'}`}>
                  {form.target_score}
                </div>
                <div className={`text-sm mt-2 ${isCyber ? 'font-mono-cyber text-neutral-400' : 'font-body-story italic text-[#68615A]'}`}>
                  {Number(form.target_score) >= 34 ? 'Elite rigor unlocked. Sleep is optional.' :
                   Number(form.target_score) >= 30 ? 'Rigorous track. Consistent daily practice required.' :
                   Number(form.target_score) >= 26 ? 'Steady track. Balanced pace.' : 'Foundational track. Build the basics.'}
                </div>
                <button data-testid="plan-next-2" onClick={() => setStep(2)} className={`mt-6 ${btnPrimary}`}>Next →</button>
              </>
            )}
            {step === 2 && (
              <>
                <div className="mb-4 text-lg">Next test date:</div>
                <input data-testid="plan-test-date" type="date" value={form.test_date}
                  onChange={e => setForm(f => ({ ...f, test_date: e.target.value }))}
                  className={`px-4 py-3 rounded-lg border-2 ${isCyber ? 'bg-black border-[#00FFD1]/40 text-[#00FFD1] font-mono-cyber' : 'bg-white border-[#C9B99A]'}`} />
                <div className="mt-6 flex gap-3">
                  <button data-testid="plan-generate" onClick={submit} disabled={!form.test_date} className={btnPrimary}>
                    {isCyber ? 'GENERATE_PLAN →' : 'Forge My Journey'}
                  </button>
                </div>
                {err && <div className="mt-3 text-red-500 text-sm">{err}</div>}
              </>
            )}
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className={cardCls}>
              <div className={isCyber ? 'font-mono-cyber text-[#00FFD1] text-xs' : 'font-body-story italic text-[#68615A]'}>
                Rigor: {plan.rigor} · Core: {plan.core_subject} · Weekly Hours: {plan.weekly_hours}
              </div>
              <div className={isCyber ? 'font-orbitron text-3xl mt-1' : 'font-storybook text-3xl mt-1'}>
                Target: {plan.target_score} · Test: {plan.test_date}
              </div>
            </div>
            {plan.weeks.map((w) => (
              <div key={w.week} data-testid={`plan-week-${w.week}`} className={cardCls}>
                <div className="flex items-center justify-between">
                  <div className={isCyber ? 'font-orbitron text-lg' : 'font-storybook text-xl'}>
                    Week {w.week} · {w.phase}
                  </div>
                  <div className={isCyber ? 'font-mono-cyber text-[#00FFD1]' : 'font-body-story text-[#8C1C13]'}>
                    {w.total_hours}h
                  </div>
                </div>
                <div className="mt-2 text-sm opacity-80">{w.milestone}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(w.focus).map(([s, h]) => (
                    <span key={s} className={`text-xs px-2 py-1 rounded ${isCyber ? 'bg-[#00FFD1]/10 text-[#00FFD1] font-mono-cyber' : 'bg-[#E8DCC4] text-[#2C2621] font-body-story'}`}>
                      {s}: {h}h
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
